import { useLocation, useSearchParams } from "@solidjs/router";
import { createMemo, createSignal, Show } from "solid-js";
import { QUICK_LOOK_SOURCE, SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { isAdmin } from "../../store/admin";
import { api } from "../../store/api";
import { saveDownload } from "../../store/api-download";
import { createAsyncAction } from "../../ui/asyncAction";
import { EmptyState } from "../../ui/EmptyState";
import { singleParam } from "../../ui/searchParam";
import { IssueEditor } from "../admin/IssueEditor";
import { FilterBar } from "../issues/FilterBar";
import { applyFilters, readFilters } from "../issues/filters";
import { IssuesTable } from "../issues/IssuesTable";
import { createWebsiteStore } from "../website/store";
import { IssueQuickLookDrawer } from "./QuickLookDrawer";
import { ScanRouteSurface } from "./ScanRouteChrome";
import { ScanRouteHeader } from "./ScanRouteHeader";
import { ScanShell } from "./ScanShell";

// Generous: the backend builds the whole workbook before it answers, so this is
// a stall guard, not a performance budget.
const EXPORT_TIMEOUT_MS = 120_000;

export function ScanIssues(props: { websiteId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);
  const [creating, setCreating] = createSignal(false);
  const exportAction = createAsyncAction();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const filters = createMemo(() =>
    readFilters(new URLSearchParams(location.search)),
  );
  const selectedId = createMemo(() => singleParam(search.issue));
  const rows = createMemo(() => {
    const data = context.data();
    return data ? applyFilters(data, filters()) : [];
  });
  const selectedIssue = createMemo(() => {
    const data = context.data();
    return data?.issues.find((issue) => issue.id === selectedId()) ?? null;
  });
  const selectedDrawer = createMemo(() => {
    const data = context.data();
    const issue = selectedIssue();
    return data && issue ? { data, issue } : null;
  });
  const closeQuickLook = () => setSearch({ issue: undefined });
  const exportIssues = () =>
    exportAction.run(async () => {
      // A workbook that never arrives would otherwise leave the button
      // reading "Exporting…" with no way back.
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), EXPORT_TIMEOUT_MS);
      try {
        saveDownload(await api.exportIssues(props.websiteId, abort.signal));
      } finally {
        clearTimeout(timeout);
      }
    });

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.ISSUES}>
      <ScanRouteSurface class="signal-issues-route">
        <ScanRouteHeader
          websiteId={props.websiteId}
          section="Issues"
          title="Issues"
          subtitle="Prioritised findings from the current scan."
          actions={
            <div class="issue-export-actions">
              <button
                class="btn"
                type="button"
                disabled={exportAction.busy()}
                onClick={() => void exportIssues()}
              >
                {exportAction.busy() ? "Exporting…" : "Export all issues"}
              </button>
              <Show when={isAdmin()}>
                <button
                  class="btn btn-primary"
                  type="button"
                  onClick={() => setCreating(true)}
                >
                  New issue
                </button>
              </Show>
            </div>
          }
        />

        <Show when={exportAction.error()}>
          <p class="issue-export-error" role="alert">
            {exportAction.error()}
          </p>
        </Show>

        <Show
          when={context.data()}
          keyed
          fallback={<div class="loading">Loading scan issues...</div>}
        >
          {(data) => (
            <>
              <FilterBar filters={filters()} />
              <Show
                when={rows().length > 0}
                fallback={
                  <EmptyState
                    title="No matching issues"
                    hint="Adjust the filters above to widen the scan findings."
                  />
                }
              >
                <section class="scan-panel scan-legacy-panel">
                  <div class="tbl-count">
                    {rows().length} issue{rows().length === 1 ? "" : "s"}
                  </div>
                  <IssuesTable
                    data={data}
                    issues={rows()}
                    selectedId={selectedId()}
                  />
                </section>
              </Show>
            </>
          )}
        </Show>
      </ScanRouteSurface>
      <Show when={creating()}>
        <IssueEditor
          context={context}
          issue={null}
          onClose={() => setCreating(false)}
        />
      </Show>
      <Show when={selectedDrawer()} keyed>
        {(drawer) => (
          <IssueQuickLookDrawer
            issue={drawer.issue}
            websiteId={props.websiteId}
            data={drawer.data}
            source={QUICK_LOOK_SOURCE.ISSUES}
            onClose={closeQuickLook}
          />
        )}
      </Show>
    </ScanShell>
  );
}
